import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useProgrammation } from '../../hooks/useProgrammation';
import { useAuthContext } from '../../contexts/AuthContext';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, useMediaQuery, useTheme } from "@mui/material";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PrintIcon from '@mui/icons-material/Print';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import PsychologyIcon from '@mui/icons-material/Psychology';
import { toast } from 'react-hot-toast';


const ViewProgrammation = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const currentUser = user;
  const { programmation, loading, error, updateProgrammation, deleteProgrammation } = useProgrammation(id);
  const [isOwner, setIsOwner] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const [updatingPublic, setUpdatingPublic] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Synchroniser l’état local isPublic lorsque la programmation est chargée ou mise à jour
  useEffect(() => {
    if (programmation) {
      setIsPublic(!!programmation.isPublic);
    }
  }, [programmation]);

  // Synchroniser isPublic avec la donnée Firestore à chaque changement
  useEffect(() => {
    if (programmation) {
      setIsPublic(!!programmation.isPublic);
    }
  }, [programmation]);

  // Vérifier si l'utilisateur est le propriétaire de la programmation et générer le lien de partage
  useEffect(() => {
    if (programmation && currentUser) {
      setIsOwner(programmation.userId === currentUser.uid);
      setShareLink(`https://program.footballcoachs.com/programmation/${id}/public`);
    }
  }, [programmation, currentUser, id]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareLink);
  };

  

  const handleDelete = () => {
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      if (id) {
        setIsDeleting(true);
        const success = await deleteProgrammation(id);
        if (success) {
          toast.success('Planification supprimée avec succès');
          navigate('/dashboard');
        } else {
          toast.error('Une erreur est survenue lors de la suppression');
        }
      }
    } catch (err) {
      console.error('Erreur lors de la suppression de la planification:', err);
      toast.error('Une erreur est survenue lors de la suppression');
    } finally {
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <CircularProgress size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }


    const handleTogglePublic = async () => {
    if (!programmation) return;
    setUpdatingPublic(true);
    const newValue = !isPublic;
    const success = await updateProgrammation(programmation.id, { isPublic: newValue });
    if (success) {
      setIsPublic(newValue);
    }
    setUpdatingPublic(false);
  };

  // --- toutes les fonctions utilitaires et hooks sont déclarés, on peut passer à la logique de rendu ---

if (!programmation) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-yellow-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                Programmation introuvable. Elle a peut-être été supprimée ou vous n'avez pas les droits pour la consulter.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const formatDate = (date: Date) => {
    return format(date, 'EEEE d MMMM yyyy', { locale: fr });
  };

  const getCategoryLabel = (category: string) => {
    const categories: { [key: string]: string } = {
      senior: 'Sénior',
      u19: 'U19',
      u17: 'U17',
      u15: 'U15',
      u13: 'U13',
      u11: 'U11',
      u9: 'U9',
      u7: 'U7',
    };
    return categories[category] || category;
  };

  return (
    <div className="p-7 min-h-screen bg-gray-100">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          nav, .main-nav, .sidebar, .header, .f, footer { display: none !important; }
          body { background: #fff !important; }
          @page { size: landscape; }
        }
      `}</style>
      <div className="max-w-full mx-auto px-0 sm:px-5">
        {/* Titre principal en majuscules */}
        <div className="text-center mb-4">
          <h1 className="text-3xl font-bold text-black uppercase print:text-black">{programmation.title}</h1>
        </div>
        
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            {/* Dates et catégorie */}
            <div className="mt-1 mx-auto flex flex-wrap items-center justify-center text-center w-full gap-3 text-black print:text-black">
              <div className="flex items-center justify-center gap-2 text-sm text-black print:text-black">
                <svg
                  className="flex-shrink-0 h-5 w-5 text-black"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                    clipRule="evenodd"
                  />
                </svg>
                {formatDate(programmation.startDate)} - {formatDate(programmation.endDate)}
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border border-black text-black print:text-black">
                {getCategoryLabel(programmation.category)}
              </span>
</div>
            </div>
          </div>
          
          {isOwner && (
            <div className="mt-4 no-print w-full grid grid-cols-3 items-center">
              <div />
              <div className="flex justify-center gap-2">
                <IconButton
                  component={Link}
                  to={`/programmation/${id}/edit`}
                  color="primary"
                  title="Modifier"
                  sx={{
                    backgroundColor: 'rgba(63, 81, 181, 0.1)',
                    borderRadius: '8px',
                    padding: isMobile ? '8px' : '12px'
                  }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  onClick={() => window.print()}
                  title="Imprimer"
                  sx={{
                    backgroundColor: 'rgba(33, 150, 243, 0.1)',
                    borderRadius: '8px',
                    padding: isMobile ? '8px' : '12px'
                  }}
                >
                  <PrintIcon />
                </IconButton>
              </div>
              <div className="flex justify-end">
                <IconButton
                  onClick={handleDelete}
                  color="error"
                  title="Supprimer"
                  sx={{
                    backgroundColor: 'rgba(244, 67, 54, 0.1)',
                    borderRadius: '8px',
                    padding: isMobile ? '8px' : '12px'
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </div>
            </div>
          )}
        </div>

        <Dialog
          open={isDeleteDialogOpen}
          onClose={() => {
            if (!isDeleting) setIsDeleteDialogOpen(false);
          }}
          aria-labelledby="delete-programmation-title"
          aria-describedby="delete-programmation-description"
        >
          <DialogTitle id="delete-programmation-title">Supprimer la planification</DialogTitle>
          <DialogContent>
            <DialogContentText id="delete-programmation-description">
              Êtes-vous sûr de vouloir supprimer cette planification ? Cette action est irréversible.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              variant="outlined"
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              Annuler
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={confirmDelete}
              disabled={isDeleting}
            >
              Supprimer
            </Button>
          </DialogActions>
        </Dialog>

        <div className="mt-8">
          <div className="bg-white/10 shadow-xl overflow-hidden border border-blue-900">
            <div className="px-2 py-4 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-black print:text-black">
                Détails de la planification
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-black no-print print:text-black">
                Informations générales et planning des séances.
              </p>
            </div>

            <div className="px-2 py-4 sm:p-6">
              <div>
                {programmation.description && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 print:text-black">Description</h4>
                    <div className="mt-1 text-sm text-gray-800 print:text-black whitespace-pre-line">
                      {programmation.description}
                    </div>
                  </div>
                )}
                <div className="border-t border-gray-300 pt-2">
                  <h4 className="text-sm font-medium text-gray-500 mb-2">Périodes et thèmes</h4>
                  {programmation.periods && programmation.periods.length > 0 ? (
                    <div className="overflow-x-auto -mx-2 sm:mx-0">
                      <table className="min-w-full divide-y divide-blue-200 border border-blue-200 bg-white/90 shadow-sm block sm:table">
                        <thead className="hidden sm:table-header-group">
                          <tr className="bg-blue-50">
                            <th className="p-1 sm:p-2 text-left text-xs font-medium text-blue-900 uppercase tracking-wider border border-blue-200">
                              Période
                            </th>
                            <th className="p-1 sm:p-2 text-left text-xs font-medium text-blue-900 uppercase tracking-wider border border-blue-200">
                              <div className="flex items-center">
                                <SportsSoccerIcon className="h-4 w-4 mr-1" />
                                <span className="hidden sm:inline">Tactique</span>
                                <span className="sm:hidden">Tact.</span>
                              </div>
                            </th>
                            <th className="p-1 sm:p-2 text-left text-xs font-medium text-blue-900 uppercase tracking-wider border border-blue-200">
                              <div className="flex items-center">
                                <SportsSoccerIcon className="h-4 w-4 mr-1" />
                                <span className="hidden sm:inline">Technique</span>
                                <span className="sm:hidden">Tech.</span>
                              </div>
                            </th>
                            <th className="p-1 sm:p-2 text-left text-xs font-medium text-blue-900 uppercase tracking-wider border border-blue-200">
                              <div className="flex items-center">
                                <FitnessCenterIcon className="h-4 w-4 mr-1" />
                                <span className="hidden sm:inline">Athlétique</span>
                                <span className="sm:hidden">Athl.</span>
                              </div>
                            </th>
                            <th className="p-1 sm:p-2 text-left text-xs font-medium text-blue-900 uppercase tracking-wider border border-blue-200">
                              <div className="flex items-center">
                                <PsychologyIcon className="h-4 w-4 mr-1" />
                                <span className="hidden sm:inline">Mental</span>
                                <span className="sm:hidden">Ment.</span>
                              </div>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="block sm:table-row-group">
                          {programmation.periods.map((period, idx) => (
                            <tr key={idx} className="bg-white/80 border-b border-blue-100 block sm:table-row mb-4 sm:mb-0 last:mb-0">
                              <td className="p-1 sm:p-2 border border-blue-200 align-top block sm:table-cell w-full sm:w-auto sm:min-w-[170px]">
                                <div className="font-semibold text-blue-900 text-xs sm:text-sm sm:hidden mb-2">Période</div>
                                {period.title && (
                                  <div className="font-semibold text-blue-900 mb-1 text-xs sm:text-sm truncate max-w-[150px]" title={period.title}>
                                    {period.title}
                                  </div>
                                )}
                                <div className="text-xs text-gray-500 mb-1 hidden sm:block">{period.comments}</div>
                                <div className="flex gap-1 sm:gap-2 text-xs">
                                  <span className="inline-block bg-blue-50 rounded px-1 sm:px-2 py-0.5 sm:py-1 border border-blue-100">
                                    {period.weeks} sem.
                                  </span>
                                  <span className="inline-block bg-blue-50 rounded px-1 sm:px-2 py-0.5 sm:py-1 border border-blue-100">
                                    {period.sessions} séances
                                  </span>
                                </div>
                              </td>
                              {(['tactique','technique','athletique','mental'] as (keyof typeof period.themes)[]).map(themeKey => {
                                const themeIcons = {
                                  tactique: <SportsSoccerIcon className="h-4 w-4 mr-1" />,
                                  technique: <SportsSoccerIcon className="h-4 w-4 mr-1" />,
                                  athletique: <FitnessCenterIcon className="h-4 w-4 mr-1" />,
                                  mental: <PsychologyIcon className="h-4 w-4 mr-1" />
                                };
                                return (
                                <td key={themeKey} className="p-1 sm:p-2 border border-blue-200 align-top block sm:table-cell w-full sm:w-auto sm:min-w-[140px]">
                                  <div className="flex items-center font-medium text-blue-800 mb-1 capitalize text-xs sm:text-sm sm:hidden">
                                    {themeIcons[themeKey]}
                                    {themeKey}
                                  </div>
                                  <div className="font-medium text-blue-800 mb-1 capitalize text-xs sm:text-sm hidden sm:block">{themeKey}</div>
                                  {/* Itérer sur les deux thèmes de chaque catégorie */}
                                  {period.themes[themeKey].map((theme, tIdx) => (
                                    <div key={tIdx} className="text-gray-700 text-xs sm:text-sm mb-1 sm:mb-2">
                                      {theme.label === '__custom__' 
                                        ? theme.customLabel || <span className="italic text-gray-400 text-xs">Personnalisé</span>
                                        : theme.label || <span className="italic text-gray-400 text-xs">-</span>}
                                    </div>
                                  ))}
                                </td>
                              )})}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="mt-4 bg-gray-50 p-4 rounded-sm text-gray-500 italic">
                      Aucune période définie.
                    </div>
                  )}
                </div>
                {isOwner && (
                  <div className="mt-6 sm:mt-8 flex items-center gap-4 no-print">
                    <button
                      onClick={handleTogglePublic}
                      disabled={updatingPublic || !programmation}
                      className={`px-4 py-2 rounded font-semibold text-sm transition-all duration-200 shadow ${isPublic ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-300 text-gray-700 hover:bg-gray-400'} ${updatingPublic ? 'opacity-60 cursor-wait' : ''}`}
                    >
                      {isPublic ? 'Rendre Privé' : 'Rendre Public'}
                    </button>
                    <span className={`text-xs font-medium ${isPublic ? 'text-green-700' : 'text-gray-500'}`}>
                      {isPublic ? 'Actuellement : Public' : 'Actuellement : Privé'}
                    </span>
                  </div>
                )}
                <div className="mt-6 sm:mt-8 no-print">
                  <h4 className="text-sm font-medium text-gray-500">Lien de partage</h4>
                  <div className="mt-1 text-sm text-gray-900 whitespace-pre-line flex items-center">
                    <input
                      type="text"
                      readOnly
                      value={shareLink}
                      className="focus:ring-primary-500 focus:border-primary-500 block w-full rounded-sm pl-2 sm:pl-3 pr-12 text-xs sm:text-sm border-gray-300"
                      placeholder="Lien de partage"
                    />
                    <IconButton
                      onClick={handleCopyLink}
                      title="Copier le lien"
                      size="small"
                      sx={{ 
                        backgroundColor: 'rgba(158, 158, 158, 0.1)', 
                        borderRadius: '4px',
                        padding: '6px',
                        ml: 1
                      }}
                    >
                      <ContentCopyIcon className="h-5 w-5 text-gray-500" />
                    </IconButton>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    Ce lien est accessible à tous ceux qui le possèdent. Ne le partagez qu'avec des personnes de confiance.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewProgrammation;
